import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ThemeInput, Theme, ColorScheme, BorderRadius, Shadow, FontWeight } from '../types'
import { useFontLoader } from '../hooks/useFontLoader'
import { LandingPagePreview } from '../previews/LandingPagePreview'
import { StylePicker } from '../components/StylePicker'
import { ColorTableEditor, ColorKey } from '../components/ColorEditor'
import { COLOR_PRESETS, TAILWIND_PRESETS, CURATED_PRESETS } from '../data/colorPresets'
import { pairingsApi } from '../../font-explorer/api/pairingsApi'
import { PairingCategory, FontPairing } from '../../font-explorer/pairings-data'
import { GOOGLE_FONTS, buildGoogleFontsUrl, GoogleFont } from '../../font-explorer/fonts'

type Step = 1 | 2 | 3
type TypographyMode = 'pairing' | 'custom'

const STEP_LABELS = ['Typography', 'Colors', 'Details']

const DEFAULT_COLORS: { lightMode: ColorScheme; darkMode: ColorScheme } = {
  lightMode: {
    primary: '#3b82f6', secondary: '#8b5cf6', accent: '#f59e0b',
    background: '#ffffff', surface: '#f8fafc', text: '#0f172a', textMuted: '#64748b',
  },
  darkMode: {
    primary: '#60a5fa', secondary: '#a78bfa', accent: '#fbbf24',
    background: '#0f172a', surface: '#1e293b', text: '#f8fafc', textMuted: '#94a3b8',
  },
}

interface CreateThemePageProps {
  onCreate: (input: ThemeInput) => Promise<Theme>
  isPairingFavorited?: (numericId: number) => boolean
  isPairingSortFavorited?: (numericId: number) => boolean
  togglePairing?: (numericId: number) => void
  isFontFavorited?: (family: string) => boolean
  isFontSortFavorited?: (family: string) => boolean
  toggleFont?: (family: string) => void
  authenticated?: boolean
  pairingSortSnapshot?: Set<number>
  fontSortSnapshot?: Set<string>
}

export function CreateThemePage({
  onCreate,
  isPairingFavorited,
  isPairingSortFavorited,
  togglePairing,
  isFontFavorited,
  isFontSortFavorited,
  toggleFont,
  authenticated,
  pairingSortSnapshot,
  fontSortSnapshot,
}: CreateThemePageProps) {
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>(1)

  // Typography state
  const [typographyMode, setTypographyMode] = useState<TypographyMode>('pairing')
  const [displayFont, setDisplayFont] = useState('Inter')
  const [bodyFont, setBodyFont] = useState('Inter')
  const [displayWeight, setDisplayWeight] = useState<FontWeight>('bold')
  const [bodyWeight, setBodyWeight] = useState<FontWeight>('normal')

  // Colors state
  const [lightMode, setLightMode] = useState<ColorScheme>(DEFAULT_COLORS.lightMode)
  const [darkMode, setDarkMode] = useState<ColorScheme>(DEFAULT_COLORS.darkMode)
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null)

  // Details state
  const [themeName, setThemeName] = useState('')
  const [description, setDescription] = useState('')
  const [borderRadius, setBorderRadius] = useState<BorderRadius>('md')
  const [shadow, setShadow] = useState<Shadow>('md')

  // Pairings data for step 1
  const [pairingCategories, setPairingCategories] = useState<PairingCategory[]>([])
  const [pairingSearch, setPairingSearch] = useState('')
  const [selectedPairingId, setSelectedPairingId] = useState<string | null>(null)

  // Custom font search for step 1
  const [displaySearch, setDisplaySearch] = useState('')
  const [bodySearch, setBodySearch] = useState('')
  const [customFontsLoaded, setCustomFontsLoaded] = useState<Set<string>>(new Set())

  // Preview state
  const [previewDark, setPreviewDark] = useState(false)
  const [creating, setCreating] = useState(false)

  // Load Google Fonts for custom picker
  const displayMatches = useMemo(() => {
    if (!displaySearch) return []
    const q = displaySearch.toLowerCase()
    let matches = GOOGLE_FONTS.filter(f => f.family.toLowerCase().includes(q))
    if (isFontSortFavorited) {
      matches = [...matches].sort((a, b) => {
        const aFav = isFontSortFavorited(a.family)
        const bFav = isFontSortFavorited(b.family)
        if (aFav && !bFav) return -1
        if (!aFav && bFav) return 1
        return 0
      })
    }
    return matches.slice(0, 12)
  }, [displaySearch, isFontSortFavorited, fontSortSnapshot])

  const bodyMatches = useMemo(() => {
    if (!bodySearch) return []
    const q = bodySearch.toLowerCase()
    let matches = GOOGLE_FONTS.filter(f => f.family.toLowerCase().includes(q))
    if (isFontSortFavorited) {
      matches = [...matches].sort((a, b) => {
        const aFav = isFontSortFavorited(a.family)
        const bFav = isFontSortFavorited(b.family)
        if (aFav && !bFav) return -1
        if (!aFav && bFav) return 1
        return 0
      })
    }
    return matches.slice(0, 12)
  }, [bodySearch, isFontSortFavorited, fontSortSnapshot])

  // Load custom font matches
  useEffect(() => {
    const toLoad = [...displayMatches, ...bodyMatches]
      .map(f => f.family)
      .filter(f => !customFontsLoaded.has(f))
    if (toLoad.length > 0) {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = buildGoogleFontsUrl(toLoad)
      document.head.appendChild(link)
      setCustomFontsLoaded(prev => {
        const next = new Set(prev)
        toLoad.forEach(f => next.add(f))
        return next
      })
    }
  }, [displayMatches, bodyMatches, customFontsLoaded])

  // Load the selected fonts for preview
  useFontLoader([displayFont, bodyFont])

  // Fetch pairings on mount
  useEffect(() => {
    pairingsApi.listCategories().then(setPairingCategories).catch(() => {})
  }, [])

  // Build the preview theme object
  const previewTheme: Theme = {
    id: 'preview',
    slug: 'preview',
    name: themeName || 'Untitled Theme',
    description,
    displayFont,
    bodyFont,
    displayWeight,
    bodyWeight,
    lightMode,
    darkMode,
    borderRadius,
    shadow,
    createdAt: '',
    updatedAt: '',
  }

  // All pairings flat
  const allPairings = useMemo(() => {
    const result: (FontPairing & { categoryName: string })[] = []
    for (const cat of pairingCategories) {
      for (const p of cat.pairings) {
        result.push({ ...p, categoryName: cat.name })
      }
    }
    return result
  }, [pairingCategories])

  const filteredPairings = useMemo(() => {
    let pairings = allPairings
    if (pairingSearch) {
      const q = pairingSearch.toLowerCase()
      pairings = pairings.filter(
        p =>
          p.displayFont.toLowerCase().includes(q) ||
          p.bodyFont.toLowerCase().includes(q) ||
          (p.name && p.name.toLowerCase().includes(q)) ||
          p.categoryName.toLowerCase().includes(q)
      )
    }
    // Sort favorites first (uses frozen snapshot so toggling doesn't reshuffle)
    if (isPairingSortFavorited) {
      pairings = [...pairings].sort((a, b) => {
        const aFav = a.numericId ? isPairingSortFavorited(a.numericId) : false
        const bFav = b.numericId ? isPairingSortFavorited(b.numericId) : false
        if (aFav && !bFav) return -1
        if (!aFav && bFav) return 1
        return 0
      })
    }
    return pairings
  }, [allPairings, pairingSearch, isPairingSortFavorited, pairingSortSnapshot])

  const selectPairing = (p: FontPairing) => {
    setDisplayFont(p.displayFont)
    setBodyFont(p.bodyFont)
    setDisplayWeight('bold')
    setBodyWeight('normal')
    setSelectedPairingId(p.id)
    if (p.name && !themeName) {
      setThemeName(p.name)
    }
  }

  const applyPreset = (preset: typeof COLOR_PRESETS[0]) => {
    setLightMode(preset.lightMode)
    setDarkMode(preset.darkMode)
    setSelectedPreset(preset.name)
    if (!themeName) {
      setThemeName(preset.name)
    }
  }

  const handleColorChange = (mode: 'lightMode' | 'darkMode', key: ColorKey, value: string) => {
    setSelectedPreset(null)
    if (mode === 'lightMode') {
      setLightMode(prev => ({ ...prev, [key]: value }))
    } else {
      setDarkMode(prev => ({ ...prev, [key]: value }))
    }
  }

  const canProceed = () => {
    if (step === 1) return !!displayFont && !!bodyFont
    if (step === 2) return true
    if (step === 3) return !!themeName.trim()
    return false
  }

  const handleCreate = async () => {
    if (!themeName.trim()) return
    setCreating(true)
    try {
      const input: ThemeInput = {
        name: themeName.trim(),
        slug: '',
        description: description || undefined,
        displayFont,
        bodyFont,
        displayWeight,
        bodyWeight,
        lightMode,
        darkMode,
        borderRadius,
        shadow,
      }
      const created = await onCreate(input)
      navigate(`/themes/${created.slug}/edit`)
    } catch {
      setCreating(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header with progress */}
      <header className="bg-white border-b border-gray-200 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3 mb-4">
            <button
              onClick={() => window.location.href = '/themes'}
              className="p-1 rounded-md hover:bg-gray-100 transition-colors cursor-pointer text-gray-500 hover:text-gray-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-lg font-semibold text-gray-900">Create Theme</h1>
          </div>

          {/* Progress steps */}
          <div className="flex items-center gap-2">
            {STEP_LABELS.map((label, i) => {
              const stepNum = (i + 1) as Step
              const isActive = step === stepNum
              const isComplete = step > stepNum

              return (
                <React.Fragment key={label}>
                  {i > 0 && (
                    <div className={`flex-1 h-px max-w-12 ${isComplete ? 'bg-gray-900' : 'bg-gray-200'}`} />
                  )}
                  <button
                    onClick={() => {
                      if (isComplete || isActive) setStep(stepNum)
                    }}
                    disabled={!isComplete && !isActive}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      isActive
                        ? 'bg-gray-900 text-white'
                        : isComplete
                        ? 'bg-gray-100 text-gray-900 hover:bg-gray-200 cursor-pointer'
                        : 'text-gray-400 cursor-default'
                    }`}
                  >
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      isActive
                        ? 'bg-white text-gray-900'
                        : isComplete
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-200 text-gray-400'
                    }`}>
                      {isComplete ? (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        stepNum
                      )}
                    </span>
                    {label}
                  </button>
                </React.Fragment>
              )
            })}
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Step content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-4 py-6">
            {/* Top navigation */}
            <div className="flex justify-between items-center mb-6">
              <button
                onClick={() => step > 1 ? setStep((step - 1) as Step) : window.location.href = '/themes'}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
              >
                {step === 1 ? 'Cancel' : 'Back'}
              </button>

              {step < 3 ? (
                <button
                  onClick={() => setStep((step + 1) as Step)}
                  disabled={!canProceed()}
                  className="px-5 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleCreate}
                  disabled={!canProceed() || creating}
                  className="px-5 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  {creating ? 'Creating...' : 'Create Theme'}
                </button>
              )}
            </div>

            {step === 1 && (
              <TypographyStep
                typographyMode={typographyMode}
                setTypographyMode={setTypographyMode}
                displayFont={displayFont}
                bodyFont={bodyFont}
                displaySearch={displaySearch}
                setDisplaySearch={setDisplaySearch}
                bodySearch={bodySearch}
                setBodySearch={setBodySearch}
                displayMatches={displayMatches}
                bodyMatches={bodyMatches}
                customFontsLoaded={customFontsLoaded}
                setDisplayFont={setDisplayFont}
                setBodyFont={setBodyFont}
                setDisplayWeight={setDisplayWeight}
                setBodyWeight={setBodyWeight}
                filteredPairings={filteredPairings}
                pairingSearch={pairingSearch}
                setPairingSearch={setPairingSearch}
                selectedPairingId={selectedPairingId}
                selectPairing={selectPairing}
                isPairingFavorited={isPairingFavorited}
                togglePairing={togglePairing}
                isFontFavorited={isFontFavorited}
                toggleFont={toggleFont}
                authenticated={authenticated}
              />
            )}
            {step === 2 && (
              <ColorsStep
                lightMode={lightMode}
                darkMode={darkMode}
                selectedPreset={selectedPreset}
                applyPreset={applyPreset}
                onColorChange={handleColorChange}
                previewTheme={previewTheme}
              />
            )}
            {step === 3 && (
              <DetailsStep
                themeName={themeName}
                setThemeName={setThemeName}
                description={description}
                setDescription={setDescription}
                borderRadius={borderRadius}
                setBorderRadius={setBorderRadius}
                shadow={shadow}
                setShadow={setShadow}
              />
            )}

            {/* Navigation buttons */}
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={() => step > 1 ? setStep((step - 1) as Step) : window.location.href = '/themes'}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
              >
                {step === 1 ? 'Cancel' : 'Back'}
              </button>

              {step < 3 ? (
                <button
                  onClick={() => setStep((step + 1) as Step)}
                  disabled={!canProceed()}
                  className="px-5 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={handleCreate}
                  disabled={!canProceed() || creating}
                  className="px-5 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  {creating ? 'Creating...' : 'Create Theme'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Preview sidebar */}
        <aside className="hidden lg:block w-[420px] flex-shrink-0 border-l border-gray-200 bg-gray-100 overflow-hidden">
          <div className="p-3 border-b border-gray-200 bg-white flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500">Live Preview</span>
            <button
              onClick={() => setPreviewDark(!previewDark)}
              className="p-1.5 rounded-md hover:bg-gray-100 transition-colors cursor-pointer"
              title={previewDark ? 'Switch to Light' : 'Switch to Dark'}
            >
              {previewDark ? (
                <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
          </div>
          <div className="h-[calc(100%-44px)] overflow-auto" style={{ transform: 'scale(0.6)', transformOrigin: 'top left', width: '166.67%', height: '166.67%' }}>
            <LandingPagePreview theme={previewTheme} isDarkMode={previewDark} />
          </div>
        </aside>
      </div>
    </div>
  )
}

/* ============================================================
   Step 1: Typography
   ============================================================ */

function TypographyStep({
  typographyMode,
  setTypographyMode,
  displayFont,
  bodyFont,
  displaySearch,
  setDisplaySearch,
  bodySearch,
  setBodySearch,
  displayMatches,
  bodyMatches,
  customFontsLoaded,
  setDisplayFont,
  setBodyFont,
  setDisplayWeight,
  setBodyWeight,
  filteredPairings,
  pairingSearch,
  setPairingSearch,
  selectedPairingId,
  selectPairing,
  isPairingFavorited,
  togglePairing,
  isFontFavorited,
  toggleFont,
  authenticated,
}: {
  typographyMode: TypographyMode
  setTypographyMode: (m: TypographyMode) => void
  displayFont: string
  bodyFont: string
  displaySearch: string
  setDisplaySearch: (s: string) => void
  bodySearch: string
  setBodySearch: (s: string) => void
  displayMatches: GoogleFont[]
  bodyMatches: GoogleFont[]
  customFontsLoaded: Set<string>
  setDisplayFont: (f: string) => void
  setBodyFont: (f: string) => void
  setDisplayWeight: (w: FontWeight) => void
  setBodyWeight: (w: FontWeight) => void
  filteredPairings: (FontPairing & { categoryName: string })[]
  pairingSearch: string
  setPairingSearch: (s: string) => void
  selectedPairingId: string | null
  selectPairing: (p: FontPairing) => void
  isPairingFavorited?: (numericId: number) => boolean
  togglePairing?: (numericId: number) => void
  isFontFavorited?: (family: string) => boolean
  toggleFont?: (family: string) => void
  authenticated?: boolean
}) {
  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Choose your typography</h2>
      <p className="text-sm text-gray-500 mb-6">Pick a curated pairing or choose custom fonts for your theme.</p>

      {/* Mode tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5 mb-6">
        <button
          onClick={() => setTypographyMode('pairing')}
          className={`flex-1 px-4 py-2 text-xs font-medium rounded-md transition-colors cursor-pointer ${
            typographyMode === 'pairing'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Pick an existing pairing
        </button>
        <button
          onClick={() => setTypographyMode('custom')}
          className={`flex-1 px-4 py-2 text-xs font-medium rounded-md transition-colors cursor-pointer ${
            typographyMode === 'custom'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Choose custom fonts
        </button>
      </div>

      {typographyMode === 'pairing' ? (
        <div>
          <div className="mb-4">
            <input
              type="text"
              value={pairingSearch}
              onChange={(e) => setPairingSearch(e.target.value)}
              placeholder="Search pairings by font or category..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-2 gap-3 max-h-[500px] overflow-y-auto">
            {filteredPairings.map((p) => (
              <WizardPairingCard
                key={p.id}
                pairing={p}
                isSelected={selectedPairingId === p.id}
                onSelect={() => selectPairing(p)}
                isFavorited={p.numericId ? isPairingFavorited?.(p.numericId) : false}
                onToggleFavorite={p.numericId && togglePairing ? (e: React.MouseEvent) => { e.stopPropagation(); togglePairing(p.numericId!) } : undefined}
                authenticated={authenticated}
              />
            ))}
            {filteredPairings.length === 0 && (
              <p className="col-span-2 text-center text-sm text-gray-400 py-8">No pairings found.</p>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Display font picker */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Display Font</label>
            <input
              type="text"
              value={displaySearch}
              onChange={(e) => setDisplaySearch(e.target.value)}
              placeholder="Search Google Fonts..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
            {displaySearch && displayMatches.length > 0 && (
              <div className="mt-2 grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                {displayMatches.map(f => (
                  <button
                    key={f.family}
                    onClick={() => {
                      setDisplayFont(f.family)
                      setDisplayWeight('bold')
                      setDisplaySearch('')
                    }}
                    className={`text-left px-3 py-2.5 rounded-lg border transition-all cursor-pointer ${
                      displayFont === f.family
                        ? 'border-gray-900 bg-gray-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className="block text-base text-gray-900 truncate"
                        style={{
                          fontFamily: customFontsLoaded.has(f.family) ? `'${f.family}', sans-serif` : 'sans-serif',
                        }}
                      >
                        {f.family}
                      </span>
                      {isFontFavorited?.(f.family) && (
                        <svg className="w-3.5 h-3.5 text-red-500 flex-shrink-0 ml-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
                        </svg>
                      )}
                    </div>
                    <span className="text-[10px] text-gray-400">{f.category}</span>
                  </button>
                ))}
              </div>
            )}
            {displayFont && displayFont !== 'Inter' && (
              <p className="mt-2 text-xs text-gray-500">
                Selected: <strong>{displayFont}</strong>
              </p>
            )}
          </div>

          {/* Body font picker */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Body Font</label>
            <input
              type="text"
              value={bodySearch}
              onChange={(e) => setBodySearch(e.target.value)}
              placeholder="Search Google Fonts..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
            {bodySearch && bodyMatches.length > 0 && (
              <div className="mt-2 grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                {bodyMatches.map(f => (
                  <button
                    key={f.family}
                    onClick={() => {
                      setBodyFont(f.family)
                      setBodyWeight('normal')
                      setBodySearch('')
                    }}
                    className={`text-left px-3 py-2.5 rounded-lg border transition-all cursor-pointer ${
                      bodyFont === f.family
                        ? 'border-gray-900 bg-gray-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span
                      className="block text-sm text-gray-900 truncate"
                      style={{
                        fontFamily: customFontsLoaded.has(f.family) ? `'${f.family}', sans-serif` : 'sans-serif',
                      }}
                    >
                      The quick brown fox jumps over the lazy dog
                    </span>
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-[10px] text-gray-400">{f.family} · {f.category}</span>
                      {isFontFavorited?.(f.family) && (
                        <svg className="w-3 h-3 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {bodyFont && bodyFont !== 'Inter' && (
              <p className="mt-2 text-xs text-gray-500">
                Selected: <strong>{bodyFont}</strong>
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/* Pairing card for step 1 */
function WizardPairingCard({
  pairing,
  isSelected,
  onSelect,
  isFavorited,
  onToggleFavorite,
  authenticated,
}: {
  pairing: FontPairing & { categoryName: string }
  isSelected: boolean
  onSelect: () => void
  isFavorited?: boolean
  onToggleFavorite?: (e: React.MouseEvent) => void
  authenticated?: boolean
}) {
  // Load fonts for the card
  useFontLoader([pairing.displayFont, pairing.bodyFont])

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!authenticated) {
      window.location.href = `/users/sign-in?return_to=${encodeURIComponent(window.location.pathname)}`
      return
    }
    onToggleFavorite?.(e)
  }

  return (
    <button
      onClick={onSelect}
      className={`text-left p-4 rounded-xl border transition-all cursor-pointer relative ${
        isSelected
          ? 'border-gray-900 bg-gray-50 ring-1 ring-gray-900'
          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
      }`}
    >
      {onToggleFavorite && (
        <span
          onClick={handleFavoriteClick}
          className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100 transition-colors cursor-pointer"
        >
          {isFavorited ? (
            <svg className="w-3.5 h-3.5 text-red-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M11.645 20.91l-.007-.003-.022-.012a15.247 15.247 0 01-.383-.218 25.18 25.18 0 01-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0112 5.052 5.5 5.5 0 0116.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 01-4.244 3.17 15.247 15.247 0 01-.383.219l-.022.012-.007.004-.003.001a.752.752 0 01-.704 0l-.003-.001z" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5 text-gray-300 hover:text-red-400 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
            </svg>
          )}
        </span>
      )}
      <p
        className="text-lg text-gray-900 leading-tight mb-1 truncate"
        style={{ fontFamily: `'${pairing.displayFont}', sans-serif` }}
      >
        {pairing.name || pairing.displayFont}
      </p>
      <p
        className="text-xs text-gray-600 leading-relaxed mb-2 line-clamp-2"
        style={{ fontFamily: `'${pairing.bodyFont}', sans-serif` }}
      >
        The quick brown fox jumps over the lazy dog
      </p>
      <div className="flex items-center gap-2 text-[10px] text-gray-400">
        <span>{pairing.displayFont}</span>
        <span>+</span>
        <span>{pairing.bodyFont}</span>
      </div>
    </button>
  )
}

/* ============================================================
   Step 2: Colors
   ============================================================ */

function ColorsStep({
  lightMode,
  darkMode,
  selectedPreset,
  applyPreset,
  onColorChange,
  previewTheme,
}: {
  lightMode: ColorScheme
  darkMode: ColorScheme
  selectedPreset: string | null
  applyPreset: (preset: typeof COLOR_PRESETS[0]) => void
  onColorChange: (mode: 'lightMode' | 'darkMode', key: ColorKey, value: string) => void
  previewTheme: Theme
}) {
  const [showPalettes, setShowPalettes] = useState(true)
  const [showFineTune, setShowFineTune] = useState(false)

  const handleApplyPreset = (preset: typeof COLOR_PRESETS[0]) => {
    applyPreset(preset)
    setShowPalettes(false)
    setShowFineTune(true)
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Choose your colors</h2>
      <p className="text-sm text-gray-500 mb-6">Pick a palette to start, then fine-tune individual colors.</p>

      {/* Palettes section (collapsible) */}
      <button
        onClick={() => setShowPalettes(!showPalettes)}
        className="w-full flex items-center justify-between py-2 mb-2 cursor-pointer group"
      >
        <div className="flex items-center gap-2">
          <svg
            className={`w-3.5 h-3.5 text-gray-400 transition-transform ${showPalettes ? 'rotate-90' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-xs font-semibold text-gray-900 uppercase tracking-wider">Color Palettes</span>
        </div>
        {selectedPreset && !showPalettes && (
          <span className="text-xs text-gray-500">
            Using <span className="font-medium text-gray-700">{selectedPreset}</span>
          </span>
        )}
      </button>

      {showPalettes && (
        <div className="mb-6">
          {/* Tailwind Palettes */}
          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-2">Tailwind Colors</p>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {TAILWIND_PRESETS.map((preset) => (
              <PresetCard key={preset.name} preset={preset} isSelected={selectedPreset === preset.name} onSelect={() => handleApplyPreset(preset)} />
            ))}
          </div>

          {/* Curated Palettes */}
          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-2">Curated Palettes</p>
          <div className="grid grid-cols-2 gap-2">
            {CURATED_PRESETS.map((preset) => (
              <PresetCard key={preset.name} preset={preset} isSelected={selectedPreset === preset.name} onSelect={() => handleApplyPreset(preset)} />
            ))}
          </div>
        </div>
      )}

      {/* Fine-tune section (collapsible) */}
      <button
        onClick={() => setShowFineTune(!showFineTune)}
        className="w-full flex items-center gap-2 py-2 mb-2 cursor-pointer group"
      >
        <svg
          className={`w-3.5 h-3.5 text-gray-400 transition-transform ${showFineTune ? 'rotate-90' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <span className="text-xs font-semibold text-gray-900 uppercase tracking-wider">Fine-Tune Colors</span>
        {!showFineTune && selectedPreset && (
          <span className="text-[10px] text-gray-400 font-normal normal-case tracking-normal ml-1">— click to customize</span>
        )}
      </button>

      {showFineTune && (
        <div className="border border-gray-200 rounded-xl bg-white">
          <ColorTableEditor
            lightMode={lightMode}
            darkMode={darkMode}
            onColorChange={onColorChange}
          />
        </div>
      )}
    </div>
  )
}

/* Compact preset card for the color step */
function PresetCard({
  preset,
  isSelected,
  onSelect,
}: {
  preset: { name: string; lightMode: ColorScheme; darkMode: ColorScheme }
  isSelected: boolean
  onSelect: () => void
}) {
  return (
    <button
      onClick={onSelect}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all cursor-pointer ${
        isSelected
          ? 'border-gray-900 bg-gray-50 ring-1 ring-gray-900'
          : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex gap-0.5 flex-shrink-0">
        {(['primary', 'secondary', 'accent'] as const).map((key) => (
          <div
            key={key}
            className="w-4 h-4 rounded-full border border-gray-200"
            style={{ backgroundColor: preset.darkMode[key] }}
          />
        ))}
      </div>
      <span className="text-xs font-medium text-gray-900 truncate">{preset.name}</span>
      {isSelected && (
        <svg className="w-3.5 h-3.5 text-gray-900 ml-auto flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      )}
    </button>
  )
}

/* ============================================================
   Step 3: Details
   ============================================================ */

function DetailsStep({
  themeName,
  setThemeName,
  description,
  setDescription,
  borderRadius,
  setBorderRadius,
  shadow,
  setShadow,
}: {
  themeName: string
  setThemeName: (n: string) => void
  description: string
  setDescription: (d: string) => void
  borderRadius: BorderRadius
  setBorderRadius: (r: BorderRadius) => void
  shadow: Shadow
  setShadow: (s: Shadow) => void
}) {
  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Final details</h2>
      <p className="text-sm text-gray-500 mb-6">Name your theme, add a description, and configure style settings.</p>

      <div className="space-y-5">
        {/* Theme name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Theme Name <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={themeName}
            onChange={(e) => setThemeName(e.target.value)}
            placeholder="e.g., Dark Elegance"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            autoFocus
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description <span className="text-gray-400 font-normal">(optional)</span>
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="What is this theme for?"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          />
        </div>

        {/* Style */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-xs font-semibold text-gray-900 uppercase tracking-wider mb-4">Style</h3>
          <StylePicker
            borderRadius={borderRadius}
            shadow={shadow}
            onBorderRadiusChange={setBorderRadius}
            onShadowChange={setShadow}
          />
        </div>
      </div>
    </div>
  )
}
